// src/components/common/SitewideMessage.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../config';
import { Bell } from 'lucide-react';

const SitewideMessage = ({ zone, userRoles, className = '' }) => {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    const loadMessages = async () => {
      try {
        const now = new Date().toISOString().split('T')[0]; // Get date only (YYYY-MM-DD)
        
        // Build query - for Zone 3, we need to ensure it works for unauthenticated users
        // Use simpler query structure for better compatibility with RLS
        const { data, error } = await supabase
          .from('platform_messages')
          .select('*')
          .eq('is_active', true)
          .eq('zone', zone)
          .or(`start_date.is.null,start_date.lte.${now}`)
          .or(`end_date.is.null,end_date.gte.${now}`)
          .order('created_at', { ascending: false });

        if (error) {
          console.error(`Error loading sitewide messages for zone ${zone}:`, error);
          // Don't set messages on error, but log it for debugging
          setMessages([]);
          return;
        }

        if (data && data.length > 0) {
          // Double-check zone filtering (defensive check in case database has wrong zone values)
          const zoneFiltered = data.filter(msg => {
            const matches = msg.zone === zone;
            if (!matches) {
              console.warn(`[SitewideMessage] Message ${msg.id} has zone="${msg.zone}" but requested zone="${zone}" - SKIPPING`);
            }
            return matches;
          });
          
          console.log(`[SitewideMessage] Zone ${zone}: Found ${data.length} total messages, ${zoneFiltered.length} match zone filter`);
          
          if (zoneFiltered.length === 0) {
            setMessages([]);
            return;
          }
          
          // For Zone 3 (login page), show all messages since users aren't logged in
          if (zone === 'zone3') {
            setMessages(zoneFiltered);
          } else {
            // Filter messages that apply to this user (all modules or specific module)
            const filteredMessages = zoneFiltered.filter(msg => 
              msg.module === 'all' || 
              (msg.module === 'equity_manager' && userRoles?.some(r => r.product_name === 'equity')) ||
              (msg.module === 'dataroom' && userRoles?.some(r => r.product_name === 'dataroom')) ||
              (msg.module === 'capraise' && userRoles?.some(r => r.product_name === 'capraise'))
            );
            setMessages(filteredMessages);
          }
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error('Error loading sitewide messages:', error);
        setMessages([]);
      }
    };

    loadMessages();
  }, [zone, userRoles]);

  const getMessageStyles = (type) => {
    switch (type) {
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  if (messages.length === 0) {
    return null;
  }

  // CRITICAL: Only Zone 1 uses fixed positioning at the top
  // Zone 2 and Zone 3 must NEVER use fixed positioning
  if (zone === 'zone1') {
    // Calculate total height for padding (approximately 40px per message)
    const barHeight = messages.length * 40;
    
    return (
      <>
        <div className="w-full fixed top-0 left-0 right-0 z-50">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`w-full py-2 px-4 ${getMessageStyles(msg.type)}`}
            >
              <div className="flex items-center justify-center">
                <Bell className="h-4 w-4 mr-2 flex-shrink-0" />
                <p className="text-sm font-medium text-center">{msg.message}</p>
              </div>
            </div>
          ))}
        </div>
        {/* Add padding spacer to prevent content from going under the fixed bar */}
        <div style={{ height: `${barHeight}px` }}></div>
      </>
    );
  }

  // Zone 2 and Zone 3 use the regular layout (not fixed)
  return (
    <div className={className}>
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={`p-3 rounded-lg border-l-4 ${getMessageStyles(msg.type)}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start flex-1">
              <Bell className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium">{msg.message}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SitewideMessage;

